import React, { useEffect, useRef, useState } from "react";
import {
  IoAlertCircleOutline,
  IoPlayOutline,
  IoVolumeHighOutline,
  IoVolumeMuteOutline,
} from "react-icons/io5";

export default function NftAudio({
  autoPlay = false,
  className = "img-fluid",
  compact = false,
  cover,
  file,
  label = "Audio NFT",
  loop = true,
  mimeType,
  preload = "metadata",
  src,
}) {
  const audioRef = useRef(null);
  const [objectUrl, setObjectUrl] = useState("");
  const [isMuted, setIsMuted] = useState(true);
  const [isPlaying, setIsPlaying] = useState(false);
  const [playbackBlocked, setPlaybackBlocked] = useState(false);
  const [mediaError, setMediaError] = useState(false);

  const audioSrc = objectUrl || src;

  useEffect(() => {
    if (!file) {
      setObjectUrl("");
      return undefined;
    }

    const nextObjectUrl = URL.createObjectURL(file);
    setObjectUrl(nextObjectUrl);

    return () => {
      URL.revokeObjectURL(nextObjectUrl);
    };
  }, [file]);

  useEffect(() => {
    setIsMuted(true);
    setIsPlaying(false);
    setPlaybackBlocked(false);
    setMediaError(false);

    if (audioRef.current) {
      audioRef.current.defaultMuted = true;
      audioRef.current.muted = true;
      audioRef.current.load();
    }
  }, [audioSrc]);

  const tryMutedAutoplay = (event) => {
    if (!autoPlay || !event.currentTarget.paused) return;

    event.currentTarget.defaultMuted = true;
    event.currentTarget.muted = true;
    const playAttempt = event.currentTarget.play();
    playAttempt?.catch(() => setPlaybackBlocked(true));
  };

  const toggleSound = async (event) => {
    event.preventDefault();
    event.stopPropagation();

    const audio = audioRef.current;
    if (!audio || mediaError) return;

    if (audio.paused) {
      audio.defaultMuted = false;
      audio.muted = false;
      setIsMuted(false);

      try {
        await audio.play();
        setPlaybackBlocked(false);
      } catch (error) {
        audio.defaultMuted = true;
        audio.muted = true;
        setIsMuted(true);
        setPlaybackBlocked(true);
      }
      return;
    }

    const nextMuted = !audio.muted;
    audio.defaultMuted = nextMuted;
    audio.muted = nextMuted;
    setIsMuted(nextMuted);
  };

  const handleKeyDown = (event) => {
    if (event.key === "Enter" || event.key === " ") {
      toggleSound(event);
    }
  };

  const preventMediaMenu = (event) => {
    event.preventDefault();
  };

  const status = mediaError
    ? {
        icon: <IoAlertCircleOutline aria-hidden="true" />,
        text: "Preview unavailable on this device",
      }
    : playbackBlocked || !isPlaying
      ? {
          icon: <IoPlayOutline aria-hidden="true" />,
          text: "Tap audio to play with sound",
        }
      : isMuted
        ? {
            icon: <IoVolumeMuteOutline aria-hidden="true" />,
            text: "Tap audio for sound",
          }
        : {
            icon: <IoVolumeHighOutline aria-hidden="true" />,
            text: "Sound on · tap to mute",
          };

  return (
    <div
      className={`nft-video-player nft-audio-player${compact ? " nft-video-player--compact" : ""}`}
      onContextMenu={preventMediaMenu}
    >
      <img
        className={`${className} nft-video-player__media nft-audio-player__cover`}
        src={cover}
        alt={`${label} cover`}
        draggable={false}
        role="button"
        tabIndex={0}
        aria-label={`${label}. ${status.text}`}
        aria-pressed={isPlaying && !isMuted}
        onClick={toggleSound}
        onKeyDown={handleKeyDown}
      />

      <audio
        ref={audioRef}
        className="nft-audio-player__media"
        autoPlay={autoPlay}
        controls={false}
        controlsList="nodownload noplaybackrate noremoteplayback"
        disableRemotePlayback
        loop={loop}
        muted={isMuted}
        playsInline
        preload={preload}
        onCanPlay={tryMutedAutoplay}
        onContextMenu={preventMediaMenu}
        onError={() => setMediaError(true)}
        onPause={() => setIsPlaying(false)}
        onPlay={() => setIsPlaying(true)}
        onVolumeChange={(event) => setIsMuted(event.currentTarget.muted)}
      >
        {audioSrc && (
          <source
            key={audioSrc}
            src={audioSrc}
            type={mimeType?.startsWith("audio/") ? mimeType : undefined}
          />
        )}
        Your browser does not support inline audio playback.
      </audio>

      <button
        type="button"
        className={`nft-video-player__sound${mediaError ? " nft-video-player__sound--error" : ""}`}
        aria-label={status.text}
        onClick={toggleSound}
      >
        {status.icon}
        <span>{status.text}</span>
      </button>
    </div>
  );
}
